import type { CameraFeature } from '@tsr/shared-types';
import { humanizeAgo } from './FreshnessIndicator';

/**
 * Honest wall-tile media labels (#232 finding 4).
 *
 * The wall caption used to read `status === 'live'` (the CATALOG state) while
 * the tile body rendered whatever media the camera actually has — so a
 * snapshot camera could caption "LIVE · LIVE FEED" over a frame honestly
 * stamped 19h old. The label and the freshness stamp must derive from the SAME
 * classification the player uses, so they can never contradict.
 *
 * Classification reuses the #256 axes exactly as `CameraMedia` routes playback:
 *   • media_format='mp4_clip'  → CLIP (a looping on-demand clip is not live
 *     video — #256 founder ruling; playback keeps the clip player);
 *   • status='live' + stream_url → LIVE (a real mounted stream: HLS / MJPEG /
 *     YouTube-live);
 *   • status='live', no stream  → SNAPSHOT (still frames — includes the Q.2.4/
 *     2.5 view-time resolvers: a re-resolved current FRAME is honest as a fresh
 *     snapshot, but it is not a live FEED, so the wall never badges it LIVE);
 *   • anything else             → OFFLINE.
 *
 * Pure (callers inject `nowMs`) so the honesty rules are unit-testable.
 */

export type WallMediaKind = 'live' | 'clip' | 'snapshot' | 'offline';

/** The camera properties the classification reads — a structural subset of
 *  `CameraFeature['properties']` so tests can build minimal fixtures. */
export interface WallMediaProps {
  status: string;
  stream_url?: string | null;
  media_format?: string | null;
  captured_at?: string | null;
  last_snapshot_at?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** Lift media_format the way CameraMedia does: top-level (#256 lifted it onto
 *  list + detail payloads), falling back to metadata for older payloads. */
function mediaFormat(props: WallMediaProps): string | null {
  if (props.media_format != null) return props.media_format;
  const meta = props.metadata;
  const fromMeta = meta && typeof meta === 'object' ? meta['media_format'] : null;
  return typeof fromMeta === 'string' ? fromMeta : null;
}

/** Classify what the tile is actually showing (the #256 axes — see module doc). */
export function classifyWallMedia(props: WallMediaProps): WallMediaKind {
  if (mediaFormat(props) === 'mp4_clip') return 'clip';
  if (props.status === 'live' && props.stream_url) return 'live';
  if (props.status === 'live') return 'snapshot';
  return 'offline';
}

/** The freshest honest frame timestamp: real capture time when the source
 *  exposes one, else the live proxy-load time when the tile just served a
 *  genuinely-fresh frame (QA-3 (c) — the snapshot proxy fetches live on every
 *  view but only stamps `last_snapshot_at` server-side, so the cached value
 *  can read days stale), else our DB ingest time — exactly FreshnessIndicator's
 *  precedence, so the wall label and the panel timeline can never disagree. */
function frameAge(
  props: Pick<WallMediaProps, 'captured_at' | 'last_snapshot_at'>,
  nowMs: number,
  proxiedAtMs?: number | null,
): { verb: 'captured' | 'ingested'; ago: string } | null {
  const capturedMs = props.captured_at ? Date.parse(props.captured_at) : Number.NaN;
  if (Number.isFinite(capturedMs)) {
    return { verb: 'captured', ago: humanizeAgo(Math.max(0, nowMs - capturedMs)) };
  }
  if (typeof proxiedAtMs === 'number' && Number.isFinite(proxiedAtMs)) {
    return { verb: 'ingested', ago: humanizeAgo(Math.max(0, nowMs - proxiedAtMs)) };
  }
  const ingestedMs = props.last_snapshot_at ? Date.parse(props.last_snapshot_at) : Number.NaN;
  if (Number.isFinite(ingestedMs)) {
    return { verb: 'ingested', ago: humanizeAgo(Math.max(0, nowMs - ingestedMs)) };
  }
  return null;
}

/** What the mounted player is ACTUALLY doing right now — reported up by the
 *  players (fill mode) so the caption can never claim LIVE over a stream the
 *  body is honestly showing as down. 'offline' covers not-live / fatal-error /
 *  unavailable; 'no_video' is the stalled-over-black guard. */
export type WallPlaybackState = 'loading' | 'playing' | 'no_video' | 'offline';

export interface WallMediaLabel {
  kind: WallMediaKind;
  /** The caption's uppercase state word: LIVE / CLIP / SNAPSHOT / OFFLINE
   *  (plus CONNECTING… / NO PICTURE for a live stream's interim runtime). */
  state: string;
  /** Freshness suffix for non-live frames ("captured 19 hours ago"), or null
   *  for live video (a playing stream IS its own freshness) and for frames
   *  with no usable timestamp. */
  freshness: string | null;
  /** Whether the caption dot may pulse — ONLY confirmed-playing live video
   *  animates (wall motion law: a still frame never wears a live pulse). */
  pulse: boolean;
}

/** Build the honest caption parts for a wall camera tile. `proxiedAtMs` is the
 *  client wall-clock of the tile's last genuinely-fresh snapshot load (QA-3
 *  (c)), when the tile tracked one. `playback` is the mounted player's actual
 *  runtime state — a live-CLASSIFIED camera only captions LIVE while the
 *  player confirms frames are flowing; a catalog-live YouTube channel that
 *  isn't broadcasting reads OFFLINE, exactly like its tile body. */
export function wallMediaLabel(
  props: WallMediaProps,
  nowMs: number,
  proxiedAtMs?: number | null,
  playback?: WallPlaybackState | null,
): WallMediaLabel {
  const kind = classifyWallMedia(props);
  switch (kind) {
    case 'live': {
      switch (playback ?? 'playing') {
        case 'loading':
          return { kind, state: 'CONNECTING…', freshness: null, pulse: false };
        case 'no_video':
          return { kind, state: 'NO PICTURE', freshness: null, pulse: false };
        case 'offline':
          return { kind, state: 'OFFLINE', freshness: null, pulse: false };
        default:
          return { kind, state: 'LIVE', freshness: null, pulse: true };
      }
    }
    case 'clip': {
      const age = frameAge(props, nowMs, proxiedAtMs);
      // An on-demand clip re-generates at view time — when we have no frame
      // timestamp, say what it is rather than faking an age.
      return {
        kind,
        state: 'CLIP',
        freshness: age ? `updated ${age.ago}` : 'on-demand',
        pulse: false,
      };
    }
    case 'snapshot': {
      const age = frameAge(props, nowMs, proxiedAtMs);
      return {
        kind,
        state: 'SNAPSHOT',
        freshness: age ? `${age.verb} ${age.ago}` : null,
        pulse: false,
      };
    }
    default:
      return { kind: 'offline', state: 'OFFLINE', freshness: null, pulse: false };
  }
}

/** Convenience overload for a full CameraFeature (the tile's fetched detail). */
export function wallMediaLabelForCamera(
  camera: CameraFeature,
  nowMs: number,
  proxiedAtMs?: number | null,
  playback?: WallPlaybackState | null,
): WallMediaLabel {
  return wallMediaLabel(
    camera.properties as unknown as WallMediaProps,
    nowMs,
    proxiedAtMs,
    playback,
  );
}
